// Steuerpaket S4: fester Stand je Eigentümer und Jahr (wird bei der Freigabe gespeichert und im Portal/PDF gezeigt).
import { istWegUns, type Zuordnung } from '../eigentuemer/rollen';
import { ladeBuchungen, ladeImportStand, ladeKlaerungen, ladeSchluessel, ladeWegEinheiten } from './api';
import { berechneObjekt, EINNAHME_KATEGORIEN, KATEGORIE_TEXT, KOSTEN_KATEGORIEN, NICHT_ANLAGE_V_TEXT, type Kategorie, type NichtAnlageV } from './steuerpaket';
import { verteileWeg } from './weg';
import { berechne35a, lade35aRechnungen, type Bescheinigung35a } from './bescheinigung35a';

export const HINWEIS_KEINE_BERATUNG = 'Zusammenstellung aus der Buchhaltung, keine Steuerberatung.';
export const ERGAENZUNGEN = [
  'Absetzung für Abnutzung (AfA) des Gebäudes',
  'Schuldzinsen eigener Darlehen außerhalb der Verwaltung',
  'Grundsteuer, soweit nicht über die Verwaltung bezahlt',
  'Fahrtkosten zum Objekt',
  'Sonstige eigene Werbungskosten (z. B. Steuerberatung, Kontoführung)',
] as const;

export interface StandGruppe { kategorie: Kategorie; text: string; betrag: number; konten: { konto: string; betrag: number }[] }
export interface StandObjekt {
  titel: string;
  art: 'mv' | 'weg';
  /** z. B. „ganzes Objekt“ oder „W02 EG · MEA 415“ */
  umfang: string;
  einnahmen: StandGruppe[];
  werbungskosten: StandGruppe[];
  summeEinnahmen: number;
  summeWerbungskosten: number;
  nichtAnlageV: { text: string; betrag: number }[];
  hinweise: string[];
  buchhaltungBis: string | null;
}
export interface SteuerpaketStand {
  version: 1;
  jahr: number;
  eigentuemer: string;
  erstellt: string;
  objekte: StandObjekt[];
  /** Objekte, die mangels Daten nicht enthalten sind */
  nichtEnthalten: string[];
  ergaenzungen: readonly string[];
  hinweis: string;
  /** § 35a-Bescheinigungen je WEG (nur mit begünstigten Aufwendungen) */
  paragraph35a?: Bescheinigung35a[];
}
export interface StandVorschau { stand: SteuerpaketStand; ungeklaert: { objekt: string; anzahl: number }[] }

const r2 = (n: number) => Math.round(n * 100) / 100;

function gruppen(posten: readonly { kategorie: Kategorie; konto: string; betrag: number }[], kats: readonly Kategorie[]): StandGruppe[] {
  return kats.map((k) => {
    const konten = new Map<string, number>();
    for (const p of posten) if (p.kategorie === k) konten.set(p.konto, (konten.get(p.konto) ?? 0) + p.betrag);
    const liste = [...konten].map(([konto, betrag]) => ({ konto, betrag: r2(betrag) })).filter((x) => Math.abs(x.betrag) >= 0.005);
    return { kategorie: k, text: KATEGORIE_TEXT[k], betrag: r2(liste.reduce((s, x) => s + x.betrag, 0)), konten: liste };
  }).filter((g) => g.konten.length > 0);
}

/** Rechnet alle Objekte des Eigentümers für das Jahr und baut den Stand; meldet Ungeklärtes (Freigabe erst ohne). */
export async function baueSteuerpaketStand(eigentuemerId: string, name: string, zuordnungen: readonly Zuordnung[], jahr: number): Promise<StandVorschau> {
  const objekte: StandObjekt[] = []; const nichtEnthalten: string[] = []; const ungeklaert: StandVorschau['ungeklaert'] = [];
  for (const z of zuordnungen.filter((x) => x.rolle === 'MV' || istWegUns(x.rolle))) {
    const weg = istWegUns(z.rolle);
    const [importStand, buchungen, klaerungen, schluessel, einheiten] = await Promise.all([
      ladeImportStand(z.objektId), ladeBuchungen(z.objektId), ladeKlaerungen(z.objektId),
      weg ? ladeSchluessel(z.objektId) : Promise.resolve([]), weg ? ladeWegEinheiten(z.objektId) : Promise.resolve([]),
    ]);
    if (!importStand || buchungen.length === 0) { nichtEnthalten.push(`${z.objektName} (keine Buchhaltungsdaten)`); continue; }
    const e = berechneObjekt(buchungen, jahr, klaerungen);
    if (e.ungeklaert.length) ungeklaert.push({ objekt: z.objektName, anzahl: e.ungeklaert.length });
    const nicht = (Object.entries(e.nichtAnlageV) as [NichtAnlageV, number][]).filter(([, v]) => Math.abs(v) >= 0.005);
    if (!weg) {
      const ein = gruppen(e.posten, EINNAHME_KATEGORIEN); const wk = gruppen(e.posten, KOSTEN_KATEGORIEN);
      objekte.push({ titel: z.objektName, art: 'mv', umfang: z.ganzesObjekt ? 'ganzes Objekt' : z.einheiten.map((x) => x.bezeichnung).join(', '),
        einnahmen: ein, werbungskosten: wk, summeEinnahmen: e.einnahmen, summeWerbungskosten: e.werbungskosten,
        nichtAnlageV: nicht.filter(([k]) => k !== 'eigentuemer').map(([k, v]) => ({ text: NICHT_ANLAGE_V_TEXT[k], betrag: v })),
        hinweise: [], buchhaltungBis: importStand.wertstellung_bis });
      continue;
    }
    const meine = verteileWeg(e, einheiten, schluessel).einheiten.filter((x) => x.eigentuemer_id === eigentuemerId);
    if (!meine.length) { nichtEnthalten.push(`${z.objektName} (keine Einheit mit MEA zugeordnet)`); continue; }
    const posten = meine.flatMap((x) => x.posten);
    const mea = meine.reduce((s, x) => s + (einheiten.find((y) => y.einheit_id === x.einheit_id)?.mea ?? 0), 0);
    const wk = gruppen(posten, KOSTEN_KATEGORIEN);
    objekte.push({ titel: z.objektName, art: 'weg', umfang: `${meine.map((x) => x.bezeichnung).join(', ')} · MEA ${mea}`,
      einnahmen: [], werbungskosten: wk, summeEinnahmen: 0, summeWerbungskosten: r2(wk.reduce((s, g) => s + g.betrag, 0)),
      nichtAnlageV: [],
      hinweise: ['Anteil an den Ausgaben der WEG nach dem Schlüssel der Hausgeldabrechnung. Zuführungen zur Erhaltungsrücklage sind nicht enthalten.'],
      buchhaltungBis: importStand.wertstellung_bis });
  }
  // § 35a je WEG (unabhängig vom Immoware-Import): nur wenn begünstigte Aufwendungen vorhanden
  const paragraph35a: Bescheinigung35a[] = [];
  for (const z of zuordnungen.filter((x) => istWegUns(x.rolle))) {
    const [rechnungen, einheiten] = await Promise.all([lade35aRechnungen(z.objektId, jahr), ladeWegEinheiten(z.objektId)]);
    if (!einheiten.some((e) => e.eigentuemer_id === eigentuemerId)) continue;
    const b = berechne35a(rechnungen, einheiten, eigentuemerId, jahr, { objekt: z.objektName, eigentuemer: name });
    if (b.posten.length) paragraph35a.push(b);
  }
  return { stand: { version: 1, jahr, eigentuemer: name, paragraph35a, erstellt: new Date().toISOString(), objekte, nichtEnthalten, ergaenzungen: ERGAENZUNGEN, hinweis: HINWEIS_KEINE_BERATUNG }, ungeklaert };
}
